import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Landing from './Landing';
import LearnMore from './LearnMore';
import Signup from './Signup';
import StartTrial from './StartTrial';
import AuthPage from './auth/AuthPage';
import VerifyEmail from './auth/VerifyEmail';
import VerifySuccess from './auth/VerifySuccess';
import ResetPassword from './auth/ResetPassword';
import Dashboard from './dashboard/Dashboard';
import AdminConsole from './AdminConsole';
import SystemLogs from './SystemLogs';
import AlpacaKeys from './AlpacaKeys';
import TradeHistory from './TradeHistory';
import Settings from './Settings';
import TradingUniverse from './TradingUniverse';
import MarketScreening from './MarketScreening';
import EarningsWatchlist from './EarningsWatchlist';
import QAResults from './QAResults';
import Users from './admin/Users';
import SystemHealth from '../pages/SystemHealth';
import RequireAuth from './RequireAuth';
import RequireAdmin from './RequireAdmin';
import PrivacyPolicy from '../pages/PrivacyPolicy';
import TermsOfService from '../pages/TermsOfService';
import BillingSuccess from './BillingSuccess';
import BillingCancel from './BillingCancel';

/**
 * Main router component that handles authentication state and routing
 * This component ensures proper handling of authentication during page refresh
 */
const AuthRouter: React.FC = () => {
  const { user, isLoading } = useAuth();
  const location = useLocation();

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen bg-neutral-950 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-400 mx-auto mb-4"></div>
          <p className="text-neutral-400">Loading...</p>
        </div>
      </div>
    );
  }

  // FIXED: Only redirect to dashboard if user is on public pages (auth or landing)
  // This prevents redirecting when user refreshes on protected routes like /settings, /trade-history, etc.
  // Also allow /learn-more, /signup, and other public pages to be accessible even when logged in
  const isPublicPage = location.pathname === '/' || location.pathname === '/auth';
  
  if (user && isPublicPage) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route 
        path="/" 
        element={<Landing />} 
      />
      <Route 
        path="/learn-more" 
        element={<LearnMore />} 
      />
      <Route 
        path="/signup" 
        element={<Signup />} 
      />
      <Route 
        path="/start-trial" 
        element={<StartTrial />} 
      />
      <Route 
        path="/privacy-policy" 
        element={<PrivacyPolicy />} 
      />
      <Route 
        path="/terms-of-service" 
        element={<TermsOfService />} 
      />
      <Route 
        path="/auth" 
        element={<AuthPage />} 
      />
      <Route 
        path="/auth/verify-email" 
        element={<VerifyEmail />} 
      />
      <Route 
        path="/auth/verify-success" 
        element={<VerifySuccess />} 
      />
      <Route 
        path="/auth/reset-password" 
        element={<ResetPassword />} 
      />
      
      {/* Billing routes */}
      <Route 
        path="/billing/success" 
        element={<BillingSuccess />} 
      />
      <Route 
        path="/billing/cancel" 
        element={<BillingCancel />} 
      />
      
      {/* Protected routes */}
      <Route 
        path="/dashboard" 
        element={
          <RequireAuth>
            <Dashboard />
          </RequireAuth>
        } 
      />
      
      <Route 
        path="/alpaca-keys" 
        element={
          <RequireAuth>
            <AlpacaKeys />
          </RequireAuth>
        } 
      />
      
      <Route 
        path="/trade-history" 
        element={
          <RequireAuth>
            <TradeHistory />
          </RequireAuth>
        } 
      />
      
      <Route 
        path="/settings" 
        element={
          <RequireAuth>
            <Settings />
          </RequireAuth>
        } 
      />
      
      {/* Admin-only selector routes */}
      <Route 
        path="/admin/trading-universe" 
        element={
          <RequireAdmin>
            <TradingUniverse />
          </RequireAdmin>
        } 
      />
      
      <Route 
        path="/admin/market-screening" 
        element={
          <RequireAdmin>
            <MarketScreening />
          </RequireAdmin>
        } 
      />
      
      <Route 
        path="/admin/earnings-watchlist" 
        element={
          <RequireAdmin>
            <EarningsWatchlist />
          </RequireAdmin>
        } 
      />
      
      <Route 
        path="/admin/qa-results" 
        element={
          <RequireAdmin>
            <QAResults />
          </RequireAdmin>
        } 
      />
      
      {/* Admin-only routes */}
      <Route 
        path="/admin" 
        element={
          <RequireAdmin>
            <AdminConsole />
          </RequireAdmin>
        } 
      />
      
      <Route 
        path="/logs" 
        element={
          <RequireAuth>
            <SystemLogs />
          </RequireAuth>
        } 
      />
      
      <Route 
        path="/admin/users" 
        element={
          <RequireAdmin>
            <Users />
          </RequireAdmin>
        } 
      />
      
      <Route 
        path="/admin/system-health" 
        element={
          <RequireAdmin>
            <SystemHealth />
          </RequireAdmin>
        } 
      />
      
      {/* Catch all - redirect to dashboard if authenticated, otherwise to landing */}
      <Route 
        path="*" 
        element={<Navigate to={user ? "/dashboard" : "/"} replace />} 
      />
    </Routes>
  );
};

export default AuthRouter;
